import { Alert, AlertTitle, Box } from '@mui/material'
import { C1Button } from 'c1ui'
import { spacing } from 'c1ui/theme/C1Spacing'
import { SurfaceState } from 'lib/a2ui'
import type { A2UIComponent } from 'pbts/c1/api/a2ui/v1/components'
import { SurfaceLifecycleState } from 'pbts/c1/api/a2ui/v1/surface'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { A2UIComponentRenderer } from './A2UIComponentRenderer'
import { SurfaceFeedback } from './SurfaceFeedback'

type A2UISurfaceRendererProps = {
    surfaceState: SurfaceState
    /**
     * Called when an action is triggered (e.g., button click).
     * Return true if the action was handled locally and loading should clear immediately.
     */
    onAction?: (actionName: string, context: Record<string, string>, sourceComponentId: string) => boolean | void
    onDataChange?: (path: string, value: unknown) => void
    disabled?: boolean
    /** Whether to show the feedback UI (thumbs up/down). */
    showFeedback?: boolean
    /** Conversation ID required for feedback submission. */
    conversationId?: string
}

/**
 * Finds the root component for rendering. First tries 'root' ID,
 * then falls back to finding any component that isn't a child of another.
 */
const findRootComponent = (componentsMap: Map<string, A2UIComponent>): A2UIComponent | undefined => {
    const root = componentsMap.get('root')
    if (root) return root

    // Fallback: find component that isn't referenced as a child
    const childIds = new Set<string>()
    for (const comp of componentsMap.values()) {
        const c = comp.component
        if (c.oneofKind === 'row' && c.row.children) {
            c.row.children.ids.forEach((id): void => { childIds.add(id) })
        } else if (c.oneofKind === 'column' && c.column.children) {
            c.column.children.ids.forEach((id): void => { childIds.add(id) })
        } else if (c.oneofKind === 'card' && c.card.children) {
            c.card.children.ids.forEach((id): void => { childIds.add(id) })
        }
    }

    for (const [id, comp] of componentsMap) {
        if (!childIds.has(id)) return comp
    }

    return undefined
}

/**
 * Renders an A2UI surface with all its components.
 * Receives pre-parsed SurfaceState (with dataModel and componentsMap) from useA2UI hook.
 */
export const A2UISurfaceRenderer: FC<A2UISurfaceRendererProps> = ({
    surfaceState,
    onAction,
    onDataChange,
    disabled: propDisabled,
    showFeedback = false,
    conversationId,
}) => {
    const { surface, dataModel, componentsMap } = surfaceState

    // Surface lifecycle state handling
    const isCompleted = surface.state === SurfaceLifecycleState.COMPLETE
    const isDeleted = surface.state === SurfaceLifecycleState.DELETED
    const disabled = propDisabled || isCompleted

    // Track loading state for buttons
    const [loadingComponents, setLoadingComponents] = useState<Set<string>>(new Set())
    const loadingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

    // Error state from dataModel (server-controlled)
    const errorMessage = dataModel.error as string | undefined
    const canRetry = dataModel.can_retry as boolean | undefined
    const hasError = Boolean(errorMessage)

    // Clear loading state when surface updates (error received, state change, or server-side update)
    useEffect(() => {
        if (hasError || isCompleted) {
            setLoadingComponents(new Set())
            for (const timeout of loadingTimeoutsRef.current.values()) {
                clearTimeout(timeout)
            }
            loadingTimeoutsRef.current.clear()
        }
    }, [hasError, isCompleted, surface.state])

    // Cleanup all timeouts on unmount
    useEffect(() => () => {
        for (const timeout of loadingTimeoutsRef.current.values()) {
            clearTimeout(timeout)
        }
        loadingTimeoutsRef.current.clear()
    }, [])

    // Handle data model changes from input components
    const handleDataChange = useCallback((path: string, value: unknown): void => {
        onDataChange?.(path, value)
    }, [onDataChange])

    // Handle action dispatches from buttons
    const handleAction = useCallback(
        (actionName: string, context: Record<string, string>, sourceComponentId: string): void => {
            setLoadingComponents((prev) => new Set(prev).add(sourceComponentId))
            const handledLocally = onAction?.(actionName, context, sourceComponentId)

            // If handled locally, clear loading immediately
            if (handledLocally) {
                setLoadingComponents((prev) => {
                    const next = new Set(prev)
                    next.delete(sourceComponentId)
                    return next
                })
                return
            }

            // Fallback timeout to clear loading state in case WebSocket update is delayed
            const existing = loadingTimeoutsRef.current.get(sourceComponentId)
            if (existing) {
                clearTimeout(existing)
            }
            const timeout = setTimeout(() => {
                setLoadingComponents((prev) => {
                    const next = new Set(prev)
                    next.delete(sourceComponentId)
                    return next
                })
                loadingTimeoutsRef.current.delete(sourceComponentId)
            }, 10000) // Clear after 10s as fallback
            loadingTimeoutsRef.current.set(sourceComponentId, timeout)
        },
        [onAction],
    )

    const handleRetry = useCallback((): void => {
        // Retry clears error state - parent should handle actual retry logic
        onDataChange?.('/error', undefined)
        onDataChange?.('/can_retry', undefined)
    }, [onDataChange])

    const rootComponent = useMemo(() => findRootComponent(componentsMap), [componentsMap])

    // Don't render deleted surfaces or surfaces without root component
    if (isDeleted || !rootComponent) {
        return null
    }

    return (
        <Box
            sx={{
                opacity: isCompleted ? 0.7 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                transition: 'opacity 0.2s ease-in-out',
            }}
        >
            {hasError &&
                <Alert
                    severity="error"
                    sx={{ mb: spacing.m }}
                    action={canRetry ?
                        <C1Button color="error" onClick={handleRetry}>
                            <FormattedMessage id="a2ui_retry" defaultMessage="Retry" />
                        </C1Button>
                        : undefined}
                >
                    <AlertTitle>
                        <FormattedMessage id="a2ui_error_title" defaultMessage="Error" />
                    </AlertTitle>
                    {errorMessage}
                </Alert>
            }
            <A2UIComponentRenderer
                component={rootComponent}
                componentsMap={componentsMap}
                dataModel={dataModel}
                onDataChange={handleDataChange}
                onAction={handleAction}
                disabled={disabled}
                loadingComponents={loadingComponents}
            />
            {showFeedback && conversationId &&
                <SurfaceFeedback
                    conversationId={conversationId}
                    surfaceId={surface.surfaceId}
                />
            }
        </Box>
    )
}
